import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sun, Moon, ArrowLeft } from 'lucide-react';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { LandingNav } from '../components/landing/LandingNav';
import { LandingFooter } from '../components/landing/LandingFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SECTIONS = [
  { title: '1. Who We Are', content: 'Grassroots Coaching ("we", "our", "us") is a team management platform designed for grassroots football coaches. We provide tools for squad management, match day operations, training planning, and team communication. Our platform is accessible at grassrootscoaching.app.' },
  { title: '2. Information We Collect', content: '2.1 Information You Provide\n\n\u2022 Account information: Name, email address, and password when you register\n\u2022 Profile information: Coaching experience, coaching levels, age groups, display name\n\u2022 Team information: Team name, club badge, team colours, player details (names, positions, stats)\n\u2022 Payment information: Processed securely by Stripe; we do not store your card details\n\u2022 Communications: Messages sent through our live chat feature\n\n2.2 Information Collected Automatically\n\n\u2022 Usage data: Pages visited, features used, button clicks, and time spent on sections\n\u2022 Device information: Browser type, screen size, and operating system\n\u2022 Scroll and interaction data: Scroll depth, section views for improving user experience' },
  { title: '3. How We Use Your Information', content: 'We use your information to:\n\n\u2022 Provide and maintain our team management services\n\u2022 Process subscriptions and payments through Stripe\n\u2022 Send important account notifications and updates via email\n\u2022 Personalise your coaching dashboard and team experience\n\u2022 Improve our platform based on usage patterns and feedback\n\u2022 Provide AI-powered live chat support\n\u2022 Prevent fraud and ensure platform security' },
  { title: '4. Cookies & Local Storage', content: 'We use browser local storage to enhance your experience. You can manage your cookie preferences at any time using the cookie settings button on our website. A complete list of all cookies and local storage items used is shown in the table below.\n\nAll items are classified as Essential and are required for the website to function correctly. We do not currently deploy third-party analytics or marketing tracking cookies. Should this change in the future, we will update this policy and request your consent via the cookie banner.' },
  { title: '5. Third-Party Services', content: 'We use the following trusted third-party services to operate our platform:\n\n\u2022 Stripe \u2014 Payment processing for subscriptions. Stripe processes your payment information directly and securely. We never see or store your full card details.\n\u2022 OpenAI \u2014 Powers our AI live chat assistant. Chat messages are sent to OpenAI\'s API for processing. No personal account data is shared.\n\u2022 Resend \u2014 Email delivery service for account notifications, password resets, and team communications.' },
  { title: '6. Data Security', content: 'We take the security of your data seriously. Measures we employ include:\n\n\u2022 Passwords are hashed using industry-standard bcrypt encryption\n\u2022 All data is transmitted over HTTPS with TLS encryption\n\u2022 Authentication tokens expire and require periodic re-authentication\n\u2022 Payment data is handled entirely by PCI-DSS compliant Stripe\n\u2022 Access to admin functions is restricted by role-based permissions' },
  { title: '7. Data Retention', content: 'We retain your personal data for as long as your account is active or as needed to provide our services. If you delete your account, we will remove your personal information within 30 days, except where we are required to retain certain data for legal or regulatory purposes. Onboarding progress saved in local storage is automatically cleared upon successful registration.' },
  { title: '8. Your Rights', content: 'You have rights over your personal data. Specific rights and how they apply depend on where you live — we honour those granted by the EU & UK General Data Protection Regulation (GDPR), the Irish Data Protection Act 2018, the California Consumer Privacy Act (CCPA / CPRA), and equivalent laws elsewhere. Subject to applicable law, these typically include:\n\n\u2022 Access: Request a copy of the personal data we hold about you\n\u2022 Correction: Request correction of inaccurate or incomplete data\n\u2022 Deletion: Request deletion of your personal data ("right to be forgotten")\n\u2022 Portability: Request your data in a portable, machine-readable format\n\u2022 Objection / restriction: Object to or restrict our processing of your data for certain purposes\n\u2022 Withdraw consent: Withdraw cookie or marketing consent at any time via the cookie settings button\n\u2022 Do Not Sell or Share (CCPA): We do not sell your personal information and do not share it for cross-context behavioural advertising\n\nTo exercise any of these rights, contact us at the email address in section 11. We will respond within the timeframes required by applicable law (typically 30 days for GDPR, 45 days for CCPA).\n\nIf you are in the EU, UK, or Ireland and believe we have not handled your data properly, you have the right to lodge a complaint with your local supervisory authority. Our lead supervisory authority is the Irish Data Protection Commission (www.dataprotection.ie).' },
  { title: '9. Children\'s Privacy', content: 'Our platform is intended for use by coaches aged 18 and over. While coaches may manage teams that include players under 18, we do not knowingly collect personal information directly from children. Player data entered by coaches is the responsibility of the coach and should be managed in accordance with applicable data protection laws and with appropriate parental consent where required.' },
  { title: '10. Changes to This Policy', content: 'We may update this privacy policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date above. We encourage you to review this policy periodically.' },
  { title: '11. Contact Us', content: 'If you have any questions about this Privacy Policy, your data, or wish to exercise any of your rights, please contact us at the email address provided below.' },
];

const DEFAULT_COOKIES = [
  { name: 'token', type: 'Local Storage', purpose: 'Stores your authentication session token so you remain logged in between visits', category: 'Essential' },
  { name: 'cookie_consent', type: 'Local Storage', purpose: 'Stores your cookie consent preferences (essential, analytics, marketing choices)', category: 'Essential' },
  { name: 'cookie_consent_time', type: 'Local Storage', purpose: 'Records the timestamp of when you last set your cookie preferences, used to determine when to re-prompt', category: 'Essential' },
  { name: 'landingTheme', type: 'Local Storage', purpose: 'Remembers your preferred colour theme (dark or light) across the website', category: 'Essential' },
  { name: 'gc_onboarding_progress', type: 'Local Storage', purpose: 'Saves your onboarding form progress so you can resume if you leave and return before completing registration', category: 'Essential' },
  { name: 'chatSessionId', type: 'Local Storage', purpose: 'Maintains your live chat conversation session so message history persists during your visit', category: 'Essential' },
  { name: 'matchday_active_match_id', type: 'Local Storage', purpose: 'Tracks the currently active match day so the correct match loads when you return to the match day screen', category: 'Essential' },
];

function renderContent(text, isDark) {
  if (!text) return null;
  const paragraphs = text.split('\n\n');
  return paragraphs.map((para, pi) => {
    const lines = para.split('\n');
    const bullets = lines.filter(l => l.startsWith('\u2022'));
    const nonBullets = lines.filter(l => !l.startsWith('\u2022'));

    return (
      <div key={pi}>
        {nonBullets.map((line, li) => {
          if (!line.trim()) return null;
          const isSub = /^\d+\.\d+\s/.test(line.trim());
          return isSub
            ? <h3 key={li} className={`text-lg sm:text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} ${pi > 0 || li > 0 ? 'mt-6 mb-3' : 'mb-3'}`}>{line}</h3>
            : <p key={li} className={`${isDark ? 'text-gray-400' : 'text-gray-600'} leading-relaxed mb-3`}>{line}</p>;
        })}
        {bullets.length > 0 && (
          <ul className={`${isDark ? 'text-gray-400' : 'text-gray-600'} leading-relaxed ml-6 mb-4 space-y-1.5`}>
            {bullets.map((b, bi) => <li key={bi}>{b}</li>)}
          </ul>
        )}
      </div>
    );
  });
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');

  const mobileMenuRef = { current: null };
  const t = THEMES[theme];
  const goToSignup = () => navigate('/onboarding');
  const logoUrl = content?.branding?.logoUrl || DEFAULT_LOGO_URL;
  const appName = content?.branding?.appName || 'Grassroots Coaching';

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('landingTheme', newTheme);
  };

  useEffect(() => {
    axios.get(`${API}/landing/content`).then(r => setContent(r.data)).catch(() => {});
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isDark = theme === 'dark';
  const pp = content?.privacy_policy || {};
  const sections = pp.sections || DEFAULT_SECTIONS;
  const cookies = pp.cookies || DEFAULT_COOKIES;
  const contactEmail = pp.contact_email || 'privacy@grassrootscoaching.app';
  const cookiesSectionIndex = sections.findIndex(s => s.title.toLowerCase().includes('cookie'));

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} relative`} style={{ overflowX: 'clip' }}>
      <style>{`html { scroll-behavior: smooth; } section[id] { scroll-margin-top: 4.5rem; }`}</style>

      <button
        onClick={toggleTheme}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
          isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
        }`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {content && (
        <LandingNav
          content={content} theme={theme} t={t} user={user}
          mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen}
          mobileMenuRef={mobileMenuRef} scrollToSection={scrollToSection}
          navigate={navigate} goToSignup={goToSignup} logoUrl={logoUrl} appName={appName}
        />
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{pp.heading || 'Privacy Policy'}</h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-lg`}>
            {pp.subtitle || 'Your privacy is important to us. This policy explains how we collect, use, and protect your information.'}
          </p>
          <p className={`${isDark ? 'text-gray-500' : 'text-gray-500'} text-sm mt-3`}>
            Last updated: {pp.last_updated || 'April 2026'}
          </p>
        </div>

        {/* Back button - top */}
        <button
          onClick={() => navigate('/')}
          className={`inline-flex items-center gap-1.5 mb-4 text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          data-testid="back-btn-top"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to main page
        </button>

        {/* Policy Content */}
        <div className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} border rounded-2xl p-6 sm:p-10`}>
          {sections.map((section, index) => (
            <div key={index}>
              <h2 className={`text-2xl sm:text-3xl font-bold ${t.text} ${index === 0 ? '' : 'mt-12'} mb-4`}>
                {section.title}
              </h2>
              {renderContent(section.content, isDark)}

              {/* Render cookies table after the cookies section */}
              {index === cookiesSectionIndex && cookies.length > 0 && (
                <>
                  {/* Desktop table - hidden on mobile */}
                  <div className={`hidden sm:block overflow-x-auto rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'} mb-6 mt-4`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                          <th className={`text-left px-4 py-3 font-semibold ${t.text}`}>Name</th>
                          <th className={`text-left px-4 py-3 font-semibold ${t.text}`}>Type</th>
                          <th className={`text-left px-4 py-3 font-semibold ${t.text}`}>Purpose</th>
                          <th className={`text-left px-4 py-3 font-semibold ${t.text}`}>Category</th>
                        </tr>
                      </thead>
                      <tbody className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                        {cookies.map((cookie, ci) => (
                          <tr key={ci} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <td className="px-4 py-3 font-mono text-xs">{cookie.name}</td>
                            <td className="px-4 py-3">{cookie.type}</td>
                            <td className="px-4 py-3">{cookie.purpose}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                cookie.category === 'Essential' ? 'bg-red-100 text-red-700' :
                                cookie.category === 'Analytics' ? 'bg-blue-100 text-blue-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>{cookie.category}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card layout - visible only on mobile */}
                  <div className="sm:hidden space-y-3 mb-6 mt-4">
                    {cookies.map((cookie, ci) => (
                      <div key={ci} className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-mono text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{cookie.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            cookie.category === 'Essential' ? 'bg-red-100 text-red-700' :
                            cookie.category === 'Analytics' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>{cookie.category}</span>
                        </div>
                        <p className={`text-xs mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{cookie.type}</p>
                        <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{cookie.purpose}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Contact email after last section */}
              {index === sections.length - 1 && (
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} leading-relaxed mt-2`}>
                  <strong>Email:</strong>{' '}
                  <a href={`mailto:${contactEmail}`} className="text-[#007a33] hover:underline font-medium">
                    {contactEmail}
                  </a>
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Cross-link to Terms */}
        <p className={`text-sm mt-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          See also our{' '}
          <button
            onClick={() => navigate('/terms')}
            className="text-[#007a33] hover:underline font-medium"
            data-testid="terms-link"
          >
            Terms of Service
          </button>
          {' '}for the rules governing your use of the platform.
        </p>

        {/* Back button - bottom */}
        <button
          onClick={() => navigate('/')}
          className={`inline-flex items-center gap-1.5 mt-4 text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          data-testid="back-btn-bottom"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to main page
        </button>
      </div>

      {content && <LandingFooter content={content} logoUrl={logoUrl} appName={appName} />}
    </div>
  );
}
